/**
 * IconButton — square button that holds a single icon. Used in toolbars,
 * topbars, sidebars. Always carries an aria-label (required).
 *
 * Variants: ghost (default) / solid / soft / danger.
 * Sizes: sm (28) / md (36, default) / lg (40).
 */

import { forwardRef } from 'react'

const VARIANTS = {
  ghost:  'bg-transparent text-ink-secondary hover:text-ink-primary hover:bg-muted',
  solid:  'bg-brand text-white hover:bg-brand-hover',
  soft:   'bg-brand-soft text-brand hover:bg-brand-soft/80',
  danger: 'bg-transparent text-danger hover:bg-danger-soft',
}

const SIZES = {
  sm: 'w-7  h-7  text-sm',
  md: 'w-9  h-9  text-base',
  lg: 'w-10 h-10 text-base',
}

const IconButton = forwardRef(function IconButton(
  {
    icon: Icon,
    variant = 'ghost',
    size = 'md',
    type = 'button',
    'aria-label': ariaLabel,
    className = '',
    children,
    ...rest
  },
  ref
) {
  if (!ariaLabel && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn('IconButton requires an aria-label')
  }
  return (
    <button
      ref={ref}
      type={type}
      aria-label={ariaLabel}
      className={`
        inline-flex items-center justify-center rounded-lg
        transition-colors flex-shrink-0
        focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-1 focus-visible:ring-offset-canvas
        disabled:opacity-50 disabled:cursor-not-allowed
        ${VARIANTS[variant] || VARIANTS.ghost} ${SIZES[size] || SIZES.md} ${className}
      `}
      {...rest}
    >
      {Icon ? <Icon className="w-[55%] h-[55%]" aria-hidden="true" /> : children}
    </button>
  )
})

export default IconButton
